// Panel de propiedades de la pieza o el cable seleccionado.
import { getDef, getPins } from '../parts/index.js';
import { partProps } from '../connectivity.js';
import { WIRE_COLORS } from '../scene/wires.js';
import { escapeHtml, pinName } from './panels.js';
import { formatOhms, parseOhms } from '../parts/units.js';

export { formatOhms, parseOhms };

export class Inspector {
  constructor(app, el) {
    this.app = app;
    this.el = el;
    app.on('selection', () => this.render());
    app.on('part-props', (id) => {
      if (this.app.selection && this.app.selection.id === id) this.refreshValues();
    });
    app.on('sim', () => this.render());
    app.on('read-only', () => this.render());
    app.on('frame', () => this.refreshStatus());
    app.on('terminal', ({ id }) => {
      if (!this.term || !this.term.el.isConnected) return;
      if (id !== null && id !== this.term.id) return;
      this.term.el.textContent = app.terminals.get(this.term.id);
      this.term.el.scrollTop = this.term.el.scrollHeight;
    });
    app.on('focus-inspector', () => {
      const f = this.el.querySelector('input, select');
      if (f) f.focus();
    });
  }

  render() {
    const sel = this.app.selection;
    const el = this.el;
    if (!sel) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    el.hidden = false;
    if (sel.type === 'wire') return this.renderWire(sel.id);
    const part = this.app.project.parts.find((p) => p.id === sel.id);
    if (!part) {
      el.hidden = true;
      return;
    }
    const def = getDef(part.type);
    const props = partProps(part);
    const sim = this.app.sim.active;
    // En solo lectura se usan los controles (botones, sensores, interruptores: props «runtime»), pero no se
    // cambia el circuito.
    const ro = !!this.app.readOnly;
    let html = `<h3>${escapeHtml(def.name)}</h3><div class="sub">${escapeHtml(part.id)} · ${escapeHtml(def.description || '')}</div>`;
    if (ro) html += `<div class="field"><div class="note">Solo lectura: puedes usar sus controles al simular, pero no cambiar el circuito.</div></div>`;
    // Estado en vivo de la pieza (p. ej. «Calentando…»): lo calcula def.status(props, visual) con lo que manda la simulación.
    if (def.status) html += `<div class="field"><div class="note status" data-status hidden></div></div>`;
    for (const p of def.props) {
      if (p.hidden) continue;
      const fixed = ro && !p.runtime && !['button', 'keypad', 'terminal'].includes(p.type);
      const field = this.fieldHtml(p, props[p.key]);
      html += `<div class="field${p.runtime && sim ? ' live' : ''}" data-prop="${p.key}">${fixed ? `<fieldset class="ro-field" disabled>${field}</fieldset>` : field}</div>`;
    }
    if (def.kind === 'board' && sim) html += `<div class="field row"><button class="btn" data-act="reset-board">⟲ Reiniciar Arduino</button></div>`;
    if (def.help) html += `<div class="field"><div class="note">${def.help}</div></div>`;
    const pins = getPins(part.type, props);
    if (def.kind !== 'breadboard' && pins.length) {
      html += `<details class="field"><summary>Pines (${pins.length})</summary><div class="pins">${pins.map((x) => `<div>${escapeHtml(x.label || x.name)}</div>`).join('')}</div></details>`;
    }
    if (!ro) {
      html += `<div class="field row">
      <button class="btn small" data-act="rotate" title="Girar 90° (R)">⟲ Girar</button>
      ${def.kind !== 'board' ? '<button class="btn small" data-act="duplicate" title="Duplicar (Ctrl+D)">⧉ Duplicar</button>' : ''}
      <button class="btn small danger" data-act="delete" title="Eliminar (Supr)">Eliminar</button>
    </div>`;
    }
    el.innerHTML = html;
    this.bind(part, def);
    this.refreshStatus();
  }

  /** Actualiza el aviso de estado de la pieza seleccionada con su último estado visual (solo con la simulación activa). */
  refreshStatus() {
    const box = this.el.querySelector('[data-status]');
    if (!box) return;
    const sel = this.app.selection;
    const part = sel && sel.type === 'part' ? this.app.project.parts.find((p) => p.id === sel.id) : null;
    const entry = part && this.app.world.views.get(part.id);
    const def = part && getDef(part.type);
    const text = def && def.status && this.app.sim.active && entry && entry.lastVisual ? def.status(partProps(part), entry.lastVisual) : '';
    if (box.textContent !== (text || '')) box.textContent = text || '';
    box.hidden = !text;
  }

  fieldHtml(p, value) {
    const label = `<label class="lbl">${escapeHtml(p.label)}<b data-show>${escapeHtml(this.display(p, value))}</b></label>`;
    switch (p.type) {
      case 'select': {
        if (p.options.length <= 8) {
          return label + `<div class="chips">${p.options.map((o) => `<button class="chip${String(o.value) === String(value) ? ' active' : ''}" data-value="${escapeHtml(o.value)}">${o.color ? `<i style="background:${o.color}"></i>` : ''}${escapeHtml(o.label)}</button>`).join('')}</div>`;
        }
        return label + `<select data-input>${p.options.map((o) => `<option value="${escapeHtml(o.value)}"${String(o.value) === String(value) ? ' selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}</select>`;
      }
      case 'resistance':
        return label + `<input type="text" data-input value="${escapeHtml(formatOhms(value))}" list="dl-${p.key}" spellcheck="false"><datalist id="dl-${p.key}">${(p.presets || []).map((v) => `<option value="${formatOhms(v)}">`).join('')}</datalist>`;
      case 'slider':
        return label + `<input type="range" data-input min="${p.min}" max="${p.max}" step="${p.step || 0.01}" value="${escapeHtml(value ?? '')}">`;
      case 'number':
        return label + `<input type="number" data-input min="${p.min ?? ''}" max="${p.max ?? ''}" step="${p.step || 1}" value="${escapeHtml(value ?? '')}">`;
      case 'boolean':
        return `<label class="check"><input type="checkbox" data-input${value ? ' checked' : ''}> ${escapeHtml(p.label)}</label>`;
      case 'button':
        return `<div class="row"><button class="btn" data-hold>${escapeHtml(p.label)}</button></div>`;
      case 'keypad':
        return label + `<div class="keys" style="grid-template-columns:repeat(${p.columns || 4},1fr)">${p.keys.map((k) => `<button data-key="${escapeHtml(k)}">${escapeHtml(k)}</button>`).join('')}</div>`;
      case 'terminal':
        return `<label class="lbl">${escapeHtml(p.label)}</label><pre class="term" data-term></pre>
          <form class="term-in" data-term-form><input type="text" placeholder="Mensaje para el Arduino" autocomplete="off">
          <select title="Fin de línea"><option value="">Sin fin</option><option value="nl" selected>NL</option><option value="crnl">CR+NL</option></select>
          <button class="btn small" type="submit">Enviar</button></form>`;
      default:
        return label + `<input type="text" data-input value="${escapeHtml(value ?? '')}" placeholder="${escapeHtml(p.placeholder || '')}">`;
    }
  }

  display(p, v) {
    if (p.format) return p.format(v);
    if (p.type === 'resistance') return formatOhms(v);
    if (p.type === 'slider') {
      const n = Number(v);
      if (p.max === 1 && p.min === 0) return Math.round(n * 100) + ' %';
      return (Math.round(n * 100) / 100).toString() + (p.unit ? ' ' + p.unit : '');
    }
    if (p.type === 'select' || p.type === 'boolean' || p.type === 'button' || p.type === 'keypad' || p.type === 'terminal') return '';
    return '';
  }

  bind(part, def) {
    const el = this.el;
    for (const f of el.querySelectorAll('[data-prop]')) {
      const key = f.dataset.prop;
      const p = def.props.find((x) => x.key === key);
      f.querySelectorAll('.chip').forEach((chip) =>
        chip.addEventListener('click', () => {
          const opt = p.options.find((o) => String(o.value) === chip.dataset.value);
          this.app.setProp(part.id, key, opt ? opt.value : chip.dataset.value);
          f.querySelectorAll('.chip').forEach((c) => c.classList.toggle('active', c === chip));
        }),
      );
      const input = f.querySelector('[data-input]');
      if (input) {
        if (p.type === 'slider') {
          input.addEventListener('pointerdown', () => this.app.beginChange());
          input.addEventListener('input', () => {
            const v = Number(input.value);
            this.app.setLiveProp(part.id, key, v, false);
            f.querySelector('[data-show]').textContent = this.display(p, v);
          });
          input.addEventListener('change', () => this.app.endChange('Ajustar ' + p.label.toLowerCase()));
        } else if (p.type === 'resistance') {
          input.addEventListener('change', () => {
            const v = parseOhms(input.value);
            if (v === null) {
              this.app.toast('Escribe un valor como 220, 4.7k o 1M.', true);
              input.value = formatOhms(partProps(part)[key]);
              return;
            }
            this.app.setProp(part.id, key, v);
            input.value = formatOhms(v);
            f.querySelector('[data-show]').textContent = formatOhms(v);
          });
        } else if (p.type === 'boolean') {
          input.addEventListener('change', () => this.app.setProp(part.id, key, input.checked));
        } else if (p.type === 'number') {
          input.addEventListener('change', () => {
            let v = Number(input.value);
            if (!Number.isFinite(v)) return;
            if (p.min !== undefined) v = Math.max(p.min, v);
            if (p.max !== undefined) v = Math.min(p.max, v);
            this.app.setProp(part.id, key, v);
          });
        } else {
          input.addEventListener('change', () => this.app.setProp(part.id, key, input.value));
        }
      }
      const hold = f.querySelector('[data-hold]');
      if (hold) {
        const down = () => this.app.partInput(part.id, key, true);
        const up = () => this.app.partInput(part.id, key, false);
        hold.addEventListener('pointerdown', down);
        hold.addEventListener('pointerup', up);
        hold.addEventListener('pointerleave', up);
      }
      const term = f.querySelector('[data-term]');
      if (term) {
        term.textContent = this.app.terminals ? this.app.terminals.get(part.id) : '';
        term.scrollTop = term.scrollHeight;
        this.term = { id: part.id, el: term };
        const form = f.querySelector('[data-term-form]');
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const input = form.querySelector('input');
          const eol = { nl: '\n', crnl: '\r\n' }[form.querySelector('select').value] || '';
          if (!this.app.sim.active) {
            this.app.toast('Inicia la simulación para enviar mensajes.');
            return;
          }
          this.app.partInput(part.id, key, input.value + eol);
          if (this.app.terminals) this.app.terminals.append(part.id, `» ${input.value}\n`);
          input.value = '';
        });
      }
      f.querySelectorAll('[data-key]').forEach((b) => {
        b.addEventListener('pointerdown', () => this.app.partInput(part.id, key, { key: b.dataset.key, down: true }));
        const up = () => this.app.partInput(part.id, key, { key: b.dataset.key, down: false });
        b.addEventListener('pointerup', up);
        b.addEventListener('pointerleave', up);
      });
    }
    const act = (name, fn) => {
      const b = el.querySelector(`[data-act="${name}"]`);
      if (b) b.addEventListener('click', fn);
    };
    act('rotate', () => this.app.rotatePart(part.id));
    act('duplicate', () => this.app.duplicatePart(part.id));
    act('delete', () => this.app.deletePart(part.id));
    act('reset-board', () => this.app.sim.restart());
  }

  refreshValues() {
    const sel = this.app.selection;
    const part = sel && this.app.project.parts.find((p) => p.id === sel.id);
    if (!part) return;
    const def = getDef(part.type);
    const props = partProps(part);
    for (const f of this.el.querySelectorAll('[data-prop]')) {
      const p = def.props.find((x) => x.key === f.dataset.prop);
      if (!p) continue;
      const show = f.querySelector('[data-show]');
      if (show) show.textContent = this.display(p, props[p.key]);
      const input = f.querySelector('[data-input]');
      if (input && document.activeElement !== input) {
        if (p.type === 'slider' || p.type === 'number') input.value = props[p.key];
        else if (p.type === 'select' && input.tagName === 'SELECT') input.value = props[p.key];
      }
      f.querySelectorAll('.chip').forEach((c) => c.classList.toggle('active', c.dataset.value === String(props[p.key])));
    }
  }

  renderWire(id) {
    const w = this.app.project.wires.find((x) => x.id === id);
    if (!w) {
      this.el.hidden = true;
      return;
    }
    const a = pinName(this.app, w.a.part + ':' + w.a.pin);
    const b = pinName(this.app, w.b.part + ':' + w.b.pin);
    if (this.app.readOnly) {
      const c = WIRE_COLORS.find((x) => x.id === w.color);
      this.el.innerHTML = `<h3>Cable</h3><div class="sub">${escapeHtml(a)} → ${escapeHtml(b)}</div><div class="field"><span class="chip active" style="cursor:default"><i style="background:${c ? c.hex : '#888'}"></i>${escapeHtml(c ? c.name : w.color)}</span></div>`;
      return;
    }
    this.el.innerHTML = `<h3>Cable</h3><div class="sub">${escapeHtml(a)} → ${escapeHtml(b)}</div>
      <div class="field"><label class="lbl">Color</label><div class="chips">${WIRE_COLORS.map((c) => `<button class="chip${c.id === w.color ? ' active' : ''}" data-color="${c.id}"><i style="background:${c.hex}"></i>${c.name}</button>`).join('')}</div></div>
      <div class="field row"><button class="btn small danger" data-act="delete">Eliminar cable</button></div>`;
    this.el.querySelectorAll('[data-color]').forEach((b) => b.addEventListener('click', () => this.app.setWireColor(id, b.dataset.color)));
    this.el.querySelector('[data-act="delete"]').addEventListener('click', () => this.app.deleteWire(id));
  }
}
